import { Button, Checkbox, FormControlLabel, Grid } from "@mui/material";
import Box from "@mui/material/Box";
import * as React from "react";
import { Controller, useForm } from "react-hook-form";
import CustomProductSelect from "../../unit/forms/CustomProductSelect";
import FieldTitle from "./FieldTitle";
import SectionStyled from "./SectionStyled";
import SectionTitle from "./SectionTitle";
import { useRecordContext } from "react-admin";
import CustomProductIntTextField from "../../unit/forms/CustomProductIntTextField";
import CustomProductActionBottom from "../CustomProductActionBottom";

export default function Calculation({ onSave, defaultValues, ...props }: any) {
  const record = useRecordContext();
  const data = record?.data?.basic?.calculation;
  const {
    control,
    handleSubmit,
    formState: { errors, isDirty },
    reset,
    getValues,
  } = useForm({
    mode: "onBlur",
    defaultValues: defaultValues || {
      day_count_convention: "",
      cal_loan_terms: "fixed_number_payments",
      interest_accrual_method: "",
      sPaymentMethod: "",
      sInterestOnlyMonths: "",
      sPaymentRounding: "NO",
    },
  });
  React.useEffect(() => {
    if (data) {
      reset({
        day_count_convention: data?.day_count_convention || "Actual/Actual",
        cal_loan_terms: data?.cal_loan_terms || "fixed_number_payments",
        interest_accrual_method: data?.interest_accrual_method || "Per period",
        sPaymentMethod: data?.sPaymentMethod || "P&I throughout Term",
        sInterestOnlyMonths: data?.sInterestOnlyMonths || "",
        sPaymentRounding: data?.sPaymentRounding || "NO",
      });
    }
  }, [data, reset]);
  React.useEffect(() => {
    // console.log("Calculation isDirty changed====>:", isDirty);
    if (props?.onDirtyChange) {
      props?.onDirtyChange(props?.curMenu, isDirty);
    }
  }, [isDirty, reset]);
  const handleSaveClick = () => {
    const formData = getValues();
    formData.saveAction = "calculation";
    // console.log("Form data:", formData);
    if (onSave) {
      onSave(formData, props?.curMenu);
      reset(formData); //保存后重置表单状态，isDirty 变为 false
    }
  };

  // const handleReset = () => {
  //   reset();
  // };

  return (
    <SectionStyled id="section_calculation">
      <SectionTitle title="Calculation - Set the rules for calculating payments" />
      <Box
        sx={{
          padding: "10px",
          height: "calc(100vh - 255px)",
          overflowY: "auto",
          // background: "yellow",
        }}
      >
        <Grid container spacing={2}>
          {/* Day Count Convention */}
          <Grid item xs={12} container spacing={1}>
            <Grid item xs={3}>
              <FieldTitle title="Day Count Convention" />
            </Grid>
            <Grid item xs={4}>
              <Controller
                name="day_count_convention"
                control={control}
                rules={{ required: "This field is required" }}
                render={({ field }) => (
                  <CustomProductSelect
                    {...field}
                    id="day_count_convention"
                    variant="outlined"
                    fullWidth
                    error={!!errors.day_count_convention}
                    helperText={errors.day_count_convention?.message?.toString()}
                    optionlist={[
                      { value: "Actual/Actual", label: "Actual/Actual" },
                      { value: "Actual/360", label: "Actual/360" },
                      { value: "30/360", label: "30/360" },
                    ]}
                  />
                )}
              />
            </Grid>
            <Grid item xs={5}></Grid>
          </Grid>

          {/* Number of Payments Calculation */}
          <Grid item xs={12} container spacing={1}>
            <Grid item xs={3}>
              <FieldTitle title="Number of Payments Calculation" />
            </Grid>
            <Grid item xs={4}>
              <Controller
                name="cal_loan_terms"
                control={control}
                rules={{ required: "This field is required" }}
                render={({ field }) => (
                  <CustomProductSelect
                    {...field}
                    id="cal_loan_terms"
                    variant="outlined"
                    fullWidth
                    error={!!errors.cal_loan_terms}
                    helperText={errors.cal_loan_terms?.message?.toString()}
                    optionlist={[
                      {
                        value: "fixed_number_payments",
                        label:
                          "Fix Number of Payments given Payment Frequency and Term, then calculate Maturity Date based on First Payment Date",
                      },
                      {
                        value: "fixed_maturity_date",
                        label:
                          "Fix Maturity Date given Term and Note Date, then calculate Number of Payments based on First Payment Date",
                      },
                    ]}
                    sx={{ marginTop: "-6px" }}
                  />
                )}
              />
            </Grid>
            <Grid item xs={5}></Grid>
          </Grid>

          {/* Interest Accrual Method */}
          <Grid item xs={12} container spacing={1}>
            <Grid item xs={3}>
              <FieldTitle title="Interest Accrual Method" />
            </Grid>
            <Grid item xs={4}>
              <Controller
                name="interest_accrual_method"
                control={control}
                rules={{ required: "This field is required" }}
                render={({ field }) => (
                  <CustomProductSelect
                    {...field}
                    id="interest_accrual_method"
                    variant="outlined"
                    fullWidth
                    error={!!errors.interest_accrual_method}
                    helperText={errors.interest_accrual_method?.message?.toString()}
                    optionlist={[
                      { value: "Per period", label: "Between Periods" },
                      { value: "Per payment", label: "Between Transactions" },
                    ]}
                    sx={{ marginTop: "-6px" }}
                  />
                )}
              />
            </Grid>
            <Grid item xs={5}></Grid>
          </Grid>

          {/* Payment Method */}
          <Grid item xs={12} container spacing={1}>
            <Grid item xs={3}>
              <FieldTitle title="Payment Method" />
            </Grid>
            <Grid item xs={4}>
              <Controller
                name="sPaymentMethod"
                control={control}
                rules={{ required: "This field is required" }}
                render={({ field }) => (
                  <CustomProductSelect
                    {...field}
                    id="sPaymentMethod"
                    variant="outlined"
                    fullWidth
                    error={!!errors.sPaymentMethod}
                    helperText={errors.sPaymentMethod?.message?.toString()}
                    optionlist={[
                      {
                        value: "Interest Only throughout Term",
                        label: "Interest Only throughout Term",
                      },
                      {
                        value: "P&I throughout Term",
                        label: "P&I throughout Term",
                      },
                      {
                        value: "Interest Only and P&I during the Term",
                        label: "Interest Only and P&I during the Term",
                      },
                    ]}
                    sx={{ marginTop: "-6px" }}
                  />
                )}
              />
            </Grid>
            <Grid item xs={3}>
              {getValues("sPaymentMethod") ===
                "Interest Only and P&I during the Term" && (
                <FieldTitle title="Number of Interest Only Months" />
              )}
            </Grid>
            <Grid item xs={2}>
              {getValues("sPaymentMethod") ===
                "Interest Only and P&I during the Term" && (
                <Controller
                  name="sInterestOnlyMonths"
                  control={control}
                  render={({ field }) => (
                    <Controller
                      name="sInterestOnlyMonths"
                      control={control}
                      //>0
                      rules={{
                        validate: (value) => {
                          if (value === "") return true; // Allow empty value
                          return (
                            parseFloat(value) > 0 ||
                            "Min amount must be greater than 0"
                          );
                        },
                      }}
                      render={({ field, fieldState: { error } }) => (
                        <CustomProductIntTextField
                          {...field}
                          small
                          helperText={error?.message}
                          onChange={(eValue) => {
                            const value = eValue.toString();
                            field.onChange(
                              value === "" ? "" : parseFloat(value)
                            );
                          }}
                          sx={{ marginTop: "-6px" }}
                        />
                      )}
                    />
                  )}
                />
              )}
            </Grid>
          </Grid>

          {/* Rounding Payments */}
          <Grid item xs={12} container spacing={1}>
            <Grid item xs={3}>
              <FieldTitle title="Rounding Payments" />
            </Grid>
            <Grid item xs={4}>
              <Controller
                name="sPaymentRounding"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <Checkbox
                        {...field}
                        checked={field.value === "YES"}
                        color="primary"
                        onChange={(e) => {
                          field.onChange(e.target.checked ? "YES" : "NO");
                        }}
                      />
                    }
                    label={"Round up except for last payment"}
                  />
                )}
              />
            </Grid>
            <Grid item xs={5}></Grid>
          </Grid>

          {/* Form Actions */}
        </Grid>
      </Box>
      <CustomProductActionBottom>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSaveClick}
          disabled={!isDirty}
          sx={{ width: "120px", mt: 1 }}
        >
          Save
        </Button>
      </CustomProductActionBottom>
    </SectionStyled>
  );
}
